<template>
  <div id="app" @click="handleGlobalClick">
     <!-- Registration Modal: It will display if showRegistrationModal is true-->
      <Registration 
         v-if="showRegistrationModal" 
         @close="closeRegistrationModal"
         @registered="handleRegistered"
      />
    <!-- Preferences Modal: Edit your saved questionnaire -->
       <div v-if="showPreferencesModal" class="modal-overlay">
         <div class="modal-content">
           <Questionnaire
             :initialData="preferencesQuestionnaire"
             @submit-questionnaire="submitQuestionnaire"
        />
        </div>
      </div>

      <SelectEquipmentModal
        v-if="showSelectEquipmentModal"
        :equipment-list="combinedEquipment"
        @close="closeSelectEquipmentModal"
        @select="handleExistingEquipmentSelect"
      />


    <!-- Header -->
    <header>
      <div class="transparent-callout">
        <h1 @click="selectHome" style="cursor: pointer;">Pocket Journeyman</h1>
        <p>Veteran-owned, tradesman-built to save pros and homeowners money.</p>
      </div>
    </header>

    <div class="main-content">
      <!-- Home Screen (First Page) -->
      <div v-if="currentScreen === 'home'" class="centered-card">
        <h2>Welcome!</h2>
        <p>Choose an option to continue.</p>
        <button class="primary-button" @click="openRegistrationModal">Create Profile</button>
        <!-- Updated Free Trial button -->
        <button class="secondary-button" @click="tryForFree">Try for Free</button>
        <button class="secondary-button" @click="navigateTo('login')">Login</button>
      </div>

      <!-- Free Trial Screen (Updated) -->
      <div v-else-if="currentScreen === 'freeTrial'" class="centered-card">
        <!-- The free trial screen is now identical to the premium Action Details view -->
        <h2>{{ selectedAction }} Details</h2>
        <div v-if="(selectedAction === 'Fix' || selectedAction === 'Install') && workingOn === 'New equipment'">
          <div class="form-group">
            <label>Enter Equipment Type</label>
            <input v-model="equipmentTypeCustom" type="text" placeholder="e.g., Furnace/Dishwasher/Washing Machine/Faucet/Ceiling Fan" />
          </div>
          <div class="form-group">
            <label>Brand (optional)</label>
            <input v-model="brand" type="text" placeholder="Sign Up For Premium" :disabled="currentScreen === 'freeTrial'" />
          </div>
          <div class="form-group">
            <label>Model (optional)</label>
            <input v-model="model" type="text" placeholder="Sign Up For Premium" :disabled="currentScreen === 'freeTrial'" />
          </div>
          <div class="form-group">
            <label>Serial (optional)</label>
            <input v-model="serial" type="text" placeholder="Sign Up For Premium" :disabled="currentScreen === 'freeTrial'" />
          </div>
          <div class="form-group">
            <label>Custom Equipment Name</label>
            <input v-model="equipmentName" type="text" placeholder="Sign Up For Premium" :disabled="currentScreen === 'freeTrial'" />
          </div>
          <div class="form-group">
            <label>Equipment Location</label>
            <select v-model="equipmentType" :disabled="currentScreen === 'freeTrial'">
              <option disabled value="">Sign Up For Premium</option>
              <option v-for="option in equipmentOptions" :key="option" :value="option">{{ option }}</option>
            </select>
          </div>
          <button class="primary-button" @click="saveEquipment">Save Equipment</button>
        </div>

        <div class="form-group">
          <label>What are you Working On?</label>
          <select v-model="workingOn">
            <option disabled value="">Select an option</option>
            <template v-if="selectedAction === 'Build'">
              <option value="New Build">New Build</option>
              <option value="My Builds">My Builds</option>
            </template>
            <template v-else>
              <option value="New equipment">New equipment</option>
              <option value="Saved Equipment">Saved Equipment</option>
            </template>
          </select>
        </div>
        <div v-if="(selectedAction === 'Fix' || selectedAction === 'Install') && workingOn === 'Saved Equipment'">
          <div class="form-group">
            <label>Select Existing Equipment</label>
            <select v-model="selectedExistingEquipment">
              <option value="" disabled>Select your equipment</option>
              <option v-for="(eq, index) in combinedEquipment" :key="index" :value="eq">
                {{ eq.location }} - {{ eq.name }} ({{ eq.type }})
              </option>
            </select>
          </div>
        </div>

        <div class="form-group">
          <label>Describe what you need to {{ selectedAction.toLowerCase() }}</label>
          <textarea v-model="actionDescription" rows="4" placeholder="Enter details"></textarea>
        </div>
        <div class="form-group">
          <label>Steps Already Taken</label>
          <textarea v-model="stepsAlreadyTaken" rows="3" placeholder="Sign Up For Premium" :disabled="currentScreen === 'freeTrial'"></textarea>
        </div>

        <!-- Updated Select Response Modes for Free Trial -->
        <div class="form-group">
          <!-- Only show "All Steps" if selectedAction is NOT Fix -->
          <div v-if="selectedAction !== 'Fix'" style="display: flex; align-items: center;">
            <div class="free-trial-locked-container">
              <input type="checkbox" id="modeAll" class="free-trial-locked" :checked="freeTrialLocked.all" @click="lockCheckbox('all', $event)" />
              <span class="red-x" v-if="freeTrialLocked.all">✖</span>
            </div>
            <span style="margin-left: 8px;">All Steps <span style="color: red;">Sign Up For Premium</span></span>
          </div>
          <!-- Hide the "One Step at a Time" option when Fix is selected -->
          <div v-if="selectedAction !== 'Fix'" style="display: flex; align-items: center;">
            <input type="checkbox" id="modeOne" v-model="selectedResponseModes" value="one" />
            <span style="margin-left: 8px;">One Step at a Time</span>
          </div>
          <div style="display: flex; align-items: center; gap: 10px;">
            <button class="primary-button" @click="handleShowVideos" :disabled="selectedAction==='Fix' && !isPremium">
              <span style="font-size: 150%;">🎥</span> video
            </button>
            <button class="primary-button" @click="handleShowPictures" :disabled="selectedAction==='Fix' && !isPremium">
              <span style="font-size: 150%;">📷</span> pictures
            </button>
          </div>
        </div>
        <div v-if="!fullResponse && !oneStepResponse && !videoResponse && !picturesResponse">
          <!-- Updated submit button to open the safety modal -->
          <button class="primary-button" @click="openSafetyModal">Submit</button>
        </div>
        <div v-else>
          <div v-if="selectedResponseModes.includes('all') && fullResponse" class="response-box">
            <p><span class="emoji">{{ responseEmoji }}</span> <span v-html="fullResponse"></span></p>
          </div>
          <div v-if="selectedResponseModes.includes('one') && oneStepResponse" class="response-box">
            <p><span class="emoji">{{ responseEmoji }}</span> <span v-html="oneStepResponse"></span></p>
          </div>
          <!-- Conditionally render dynamic troubleshooting response for premium Fix flow -->
          <div v-if="selectedResponseModes.includes('one')">
            <!-- For Fix with premium, replace yes/no buttons with text input and an added "How Do I Do This?" button -->
            <div v-if="selectedAction === 'Fix' && isPremium" class="diagnostic-response">
              <input type="text" v-model="diagnosticAnswer" placeholder="Type your answer here" />
              <button class="primary-button" @click="submitDiagnosticAnswer">Submit Answer</button>
              <button class="primary-button" @click="sendExplainStep">How Do I Do This?</button>
            </div>
            <div v-else class="step-buttons">
              <button class="primary-button" @click="sendPreviousStep" :disabled="currentStepIndex === 1">Previous Step</button>
              <button class="primary-button" @click="sendExplainStep">How do I do this?</button>
              <button class="primary-button" @click="sendNextStep">Next Step</button>
            </div>
          </div>
          <div v-if="explanationResponse" class="response-box">
            <h3>Explanation:</h3>
            <p v-html="explanationResponse"></p>
          </div>
          <!-- Updated Explanation Feedback Section -->
          <div v-if="explanationResponse" class="explanation-feedback">
            <textarea v-model="explanationFeedback" placeholder="Provide additional feedback on this explanation"></textarea>
            <button class="primary-button" @click="sendExplanationFeedback">Send</button>
          </div>
          <div v-if="selectedResponseModes.includes('videos')" class="response-box">
            <h3>Video Links:</h3>
            <p v-html="videoResponse"></p>
          </div>
          <div v-if="selectedResponseModes.includes('pictures')" class="response-box">
            <h3>Relevant Pictures:</h3>
            <p v-html="picturesResponse"></p>
          </div>
        </div>

        <div class="support-buttons" v-if="fullResponse || oneStepResponse || videoResponse || picturesResponse">
          <button class="primary-button" @click="handleCallProfessional">Call a Professional</button>
          <button class="primary-button" @click="toggleMyContractorInput">Call My Contractor</button>
        </div>
        <!-- Added block for premium support UI in Action Details -->
        <div v-if="isPremium && currentSupportType === 'professional'" class="response-box">
          <div class="form-group">
            <label>Enter Zip Code for Professional Search</label>
            <input v-model="callZip" type="text" placeholder="e.g., 12345" />
          </div>
          <!-- Confirm button added below the trade dropdown -->
          <div class="form-group" v-if="showTradeDropdown">
            <label>Select a Trade</label>
            <select v-model="selectedTrade">
              <option disabled value="">Please select a trade</option>
              <option v-for="(option, index) in tradeOptions" :key="index" :value="option.title">
                {{ option.title }}
              </option>
            </select>
            <button class="primary-button" @click="confirmTradeSelection">Confirm Trade</button>
          </div>
          <div class="form-group" v-if="showMyContractorInput">
            <label>Enter Contractor Name</label>
            <input v-model="myContractorNameInput" type="text" placeholder="Enter contractor name" />
            <button class="primary-button" @click="getMyContractorByName">Get My Contractor</button>
          </div>
          <div v-if="myContractor" class="contractor-confirmation">
            <h3>Confirm Contractor Details</h3>
            <p><strong>Name:</strong> {{ myContractor.name }}</p>
            <p><strong>Address:</strong> {{ myContractor.address }}</p>
            <p><strong>Phone:</strong> {{ myContractor.phone }}</p>
            <p><strong>Type:</strong> {{ myContractor.type }}</p>
            <p><strong>Rating:</strong> {{ myContractor.rating }}</p>
            <p><strong>Total Reviews:</strong> {{ myContractor.userRatingsTotal }}</p>
            <a :href="myContractor.googleLink" target="_blank">View on Google</a>
            <button class="primary-button" @click="handleContractorSelection(myContractor)">
              Make My Contractor
            </button>
          </div>
          <div v-if="supportResponse && currentSupportType === 'professional'" class="response-box">
            <h3>Professional Recommendations:</h3>
            <ul>
              <li v-for="(contractor, index) in contractorResults" :key="index">
                <strong>{{ contractor.name }}</strong><br />
                {{ contractor.address }}<br />
                Rating: {{ contractor.rating }} ({{ contractor.userRatingsTotal }} reviews)<br />
                <a :href="contractor.googleLink" target="_blank">View on Google</a><br />
                <button class="primary-button" @click="handleContractorSelection(contractor)">
                  Make My Contractor
                </button>
              </li>
            </ul>
          </div>
        </div>
        <div v-if="errorMessageAction" class="error">{{ errorMessageAction }}</div>
        <div class="reset-button">
          <button class="secondary-button" @click="resetProcess">My Problem is Fixed</button>
        </div>
        <div v-if="showResetOptions" class="reset-options">
          <button class="secondary-button" @click="solveAnotherProblem">Solve Another Problem</button>
          <button class="secondary-button" @click="goToMainScreen">Main Screen</button>
        </div>
      </div>

      <!-- Login Screen -->
      <div v-else-if="currentScreen === 'login'" class="centered-card">
        <h2>Login</h2>
        <div class="form-group">
          <label>Email</label>
          <input v-model="loginEmail" type="email" placeholder="Enter your email" />
        </div>
        <div class="form-group">
          <label>Password</label>
          <input v-model="loginPassword" type="password" placeholder="Enter your password" />
        </div>
        <button class="primary-button" @click="loginUser">Login</button>
        <div style="margin-top: 10px; text-align: right;">
          <router-link to="/request-password-reset" class="forgot-password-link">Forgot Password?</router-link>
        </div>
        <div v-if="errorMessageLogin" class="error">{{ errorMessageLogin }}</div>
      </div>

      <!-- Subscription Screen -->
      <div v-else-if="currentScreen === 'subscription'" class="centered-card">
        <h2>Subscription</h2>
        <p>Select your plan below:</p>
        <div class="form-group">
          <label>
            <input type="radio" value="Premium/w video" v-model="selectedPlan" />
            Premium/w video – Enjoy fully customized AI support with curated video content for visual learners. Price: $9.99/month.
          </label>
        </div>
        <div class="form-group">
          <label>
            <input type="radio" value="Basic" v-model="selectedPlan" />
            Basic – Receive general guidance with limited customization. Price: $4.99/month.
          </label>
        </div>
        <div class="form-group">
          <label>
            <input type="radio" value="Pay as you go" v-model="selectedPlan" />
            Pay as you go – No customization, $0.25 per step.
          </label>
        </div>
        <!-- Premium Info is now always visible but locked for non‑premium users -->
        <div class="premium-info" :class="{ 'locked': !isPremium }">
          <p><em>Premium Subscription includes fully customized AI assistance along with curated, clickable video links for visual guidance on each step.</em></p>
        </div>
        <div class="form-group">
           <label>Promo Code</label>
           <input v-model="promoCode" type="text" placeholder="Enter promo code" />
        </div>

        <!-- Credit Card Information -->
        <div class="form-group">
          <label>Name on Card</label>
          <input v-model="cardName" type="text" placeholder="Name as it appears on card" />
        </div>
        <div class="form-group">
          <label>Credit Card Information</label>
        <div id="card-element"></div>
        </div>
        <button class="primary-button" @click="subscribeUser">Subscribe</button>
        <div v-if="subscriptionMessage" class="response-box">
          <p v-html="subscriptionMessage"></p>
        </div>
        <button class="secondary-button" @click="navigateTo('cancelSubscription')">Cancel Subscription</button>
      </div>

      <!-- Cancel Subscription Screen -->
      <div v-else-if="currentScreen === 'cancelSubscription'" class="centered-card cancel">
        <h2>Cancel Subscription</h2>
        <p>Are you sure you want to cancel your subscription?</p>
        <button class="primary-button" @click="confirmCancel">Yes, Cancel</button>
        <button class="secondary-button" @click="navigateTo('subscription')">No, Go Back</button>
        <div v-if="showCancellationForm">
          <h3>Please tell us why you're canceling:</h3>
          <div class="form-group" v-for="(option, index) in cancellationOptions" :key="index">
            <input type="checkbox" :id="'cancel' + index" v-model="selectedCancellationReasons" :value="option" />
            <label :for="'cancel' + index">{{ option }}</label>
          </div>
          <div class="form-group">
            <label>Additional Comments (Optional)</label>
            <textarea v-model="cancellationComments" rows="3" placeholder="Your feedback..."></textarea>
          </div>
          <button class="primary-button" @click="submitCancellation">Submit Cancellation</button>
          <div v-if="cancellationMessage" class="response-box">
            <p v-html="cancellationMessage"></p>
          </div>
        </div>
      </div>

      <!-- My Home Screen -->
      <div v-else-if="currentScreen === 'myHome'" class="centered-card">
        <h2>My Home</h2>
        <div class="profile-callouts">
          <div class="callout-box">
            <button class="primary-button" @click="selectMyEquipment">My Equipment</button>
          </div>
          <div class="callout-box">
            <button class="primary-button" @click="selectMyRepairs">My Repairs</button>
          </div>
          <div class="callout-box">
            <button class="primary-button" @click="selectMyBuilds">My Builds</button>
          </div>
        </div>
      </div>

      <!-- My Business Screen -->
      <div v-else-if="currentScreen === 'myBusiness'" class="centered-card">
        <h2>My Business</h2>
        <div class="profile-callouts">
          <div class="callout-box">
            <button class="primary-button" @click="selectMyEquipment">Equipment</button>
          </div>
          <div class="callout-box">
            <button class="primary-button" @click="selectMyRepairs">Repairs</button>
          </div>
          <div class="callout-box">
            <button class="primary-button" @click="selectMyBuilds">Builds</button>
          </div>
        </div>
      </div>

      <!-- Equipment View Screen -->
      <div v-else-if="currentScreen === 'equipmentView'" class="centered-card">
        <h2>My Equipment - {{ currentEquipmentLocation }}</h2>
        <div v-if="savedEquipment[currentEquipmentLocation] && savedEquipment[currentEquipmentLocation].length">
          <ul>
            <li v-for="(eq, index) in savedEquipment[currentEquipmentLocation]" :key="index">
              <strong>{{ eq.name }}</strong> ({{ eq.type }})
              <div v-if="eq.brand">Brand: {{ eq.brand }}</div>
              <div v-if="eq.model">Model: {{ eq.model }}</div>
              <div v-if="eq.serial">Serial: {{ eq.serial }}</div>
            </li>
          </ul>
        </div>
        <div v-else>
          <p>No equipment saved for {{ currentEquipmentLocation }}.</p>
        </div>
        <button class="primary-button" @click="goHome">Back</button>
      </div>

      <!-- UPDATED CONTRACTORS FEATURE: My Contractors Screen -->
      <div v-else-if="currentScreen === 'myContractors'" class="centered-card">
        <h2>My Contractors</h2>
        <div class="contractor-types" style="display: flex; flex-wrap: wrap; gap: 10px; justify-content: center;">
          <div v-for="type in contractorTypes" :key="type" class="callout-box" style="padding: 10px; width: 220px;">
            <h3>{{ type }}</h3>
            <div v-if="contractorByType(type)">
              <p><strong>{{ contractorByType(type).name }}</strong></p>
              <p>{{ contractorByType(type).address }}</p>
              <p>{{ contractorByType(type).phone }}</p>
              <p>Rating: {{ contractorByType(type).rating }} ({{ contractorByType(type).userRatingsTotal }} reviews)</p>
              <a :href="contractorByType(type).googleLink" target="_blank">View on Google</a>
              <button class="secondary-button" @click="deleteContractorByType(type)">Delete</button>
            </div>
            <div v-else>
              <p>No contractor saved.</p>
            </div>
          
            

            <!-- Conditional rendering for contractor options -->
             <div v-if="showContractorOptions && selectedContractorType === type">
              <button class="primary-button" @click="handleCallProfessional">
                Find a new contractor
             </button>
             <button class="primary-button" @click="toggleMyContractorInput">
               Find my contractor
             </button>
           </div>
           <div v-else>
             <button class="primary-button" @click="openContractorForm(type)">
               {{ contractorByType(type) ? 'Edit Contractor' : 'Add Contractor' }}
             </button>
          </div>
        </div>
   </div> 
</div>

  <!-- Profile View Screen -->
      <div v-else-if="currentScreen === 'profileView'" class="centered-card">
        <div class="profile-callouts">
          <div class="callout-box">
            <button class="primary-button" @click="navigateTo('profileDetails')">My Information</button>
          </div>
          <div class="callout-box">
            <button class="primary-button" @click="selectSubscription">Subscription</button>
          </div>
          <div class="callout-box">
            <button class="primary-button" @click="logoutUser">Logout</button>
          </div>
          <div class="callout-box">
            <button class="primary-button" @click="selectPreferences">Preferences</button>
          </div>
        </div>
      </div>

      <!-- Profile Details Screen -->
      <div v-else-if="currentScreen === 'profileDetails'" class="centered-card">
        <h2>Profile Information</h2>
        <form @submit.prevent="updateProfile">
          <div class="form-group">
            <label>Full Name</label>
            <input v-model="profileViewForm.fullName" type="text" />
          </div>
          <div class="form-group">
            <label>Email</label>
            <input v-model="profileViewForm.email" type="email" disabled />
          </div>
          <div class="form-group">
            <label>Zip Code</label>
            <input v-model="profileViewForm.zip" type="text" />
          </div>
          <div class="form-group">
            <label>Skill Level</label>
            <select v-model="profileViewForm.skillLevel">
              <option value="" disabled>Select your skill level</option>
              <option v-for="level in skillLevels" :key="level" :value="level">{{ level }}</option>
            </select>
          </div>
          <div class="form-group" v-if="profileViewForm.skillLevel === 'Service Technician'">
            <label>Occupation</label>
            <input v-model="profileViewForm.occupation" type="text" placeholder="HVAC Technician, Electrician, etc." />
          </div>
          <div class="form-group" v-if="profileViewForm.skillLevel === 'Service Technician'">
            <label>Years of Experience</label>
            <input v-model="profileViewForm.yearsOfExperience" type="text" placeholder="e.g., 5" />
          </div>
          <button class="primary-button" type="submit">Save</button>
          <button class="secondary-button" type="button" @click="cancelProfileEdit">Cancel</button>
        </form>
      </div>

      <!-- Action Selection Screen -->
      <div v-else-if="currentScreen === 'actionSelection'" class="centered-card">
        <h2 class="welcome-text">Select an option below to get started!</h2>
        <div class="action-options">
          <div class="action-option callout-box" @click="chooseAction('Build')">
            <div class="emoji">👷🏾‍♀️</div>
            <button class="primary-button" @click.stop="chooseAction('Build')">Build</button>
            <p>Build with the guidance of AI every step of the way</p>
          </div>
          <div class="action-option callout-box" @click="chooseAction('Install')">
            <div class="emoji">👷🏿‍♂️</div>
            <button class="primary-button" @click.stop="chooseAction('Install')">Install</button>
            <p>Make repairs with the knowledge and competency of AI</p>
          </div>
          <div class="action-option callout-box" @click="chooseAction('Fix')">
            <div class="emoji">👨🏻‍🔧</div>
            <button class="primary-button" @click.stop="chooseAction('Fix')">Fix</button>
            <p>Troubleshoot with the intelligence and efficiency of AI</p>
          </div>
        </div>
      </div>

      <!-- Action Details Screen -->
      <div v-else-if="currentScreen === 'actionDetails'" class="centered-card">
        <h2>{{ selectedAction }} Details</h2>

        <div v-if="(selectedAction === 'Fix' || selectedAction === 'Install') && workingOn === 'New equipment'">
          <button class="primary-button" @click="openEquipmentModal">
            Add / Edit Equipment
          </button>
        </div>


        <div class="form-group">
          <label>What are you Working On?</label>
          <select v-model="workingOn">
            <option disabled value="">Select an option</option>
            <template v-if="selectedAction === 'Build'">
              <option value="New Build">New Build</option>
              <option value="My Builds">My Builds</option>
            </template>
            <template v-else>
              <option value="New equipment">New equipment</option>
              <option value="Saved Equipment">Saved Equipment</option>
            </template>
          </select>
        </div>
        <div v-if="(selectedAction === 'Fix' || selectedAction === 'Install') && workingOn === 'Saved Equipment'">
          <button class="primary-button" @click="openSelectEquipmentModal">
            Choose Existing Equipment
          </button>
        </div>

        <div class="form-group">
          <label>Describe what you need to {{ selectedAction.toLowerCase() }}</label>
          <textarea v-model="actionDescription" rows="4" placeholder="Enter details"></textarea>
        </div>
        <div class="form-group">
          <label>Steps Already Taken</label>
          <textarea v-model="stepsAlreadyTaken" rows="3" placeholder="Enter the steps you have already taken"></textarea>
        </div>

        <!-- Updated Select Response Modes for Action Details -->
        <div class="form-group">
          <!-- Only show "All Steps" if selectedAction is NOT Fix -->
          <div v-if="selectedAction !== 'Fix'" style="display: flex; align-items: center;">
            <input type="checkbox" id="modeAll" v-model="selectedResponseModes" value="all" :disabled="selectedAction==='Fix'" />
            <span style="margin-left: 8px;">All Steps</span>
          </div>
          <!-- Hide the "One Step at a Time" option when Fix is selected -->
          <div v-if="selectedAction !== 'Fix'" style="display: flex; align-items: center;">
            <input type="checkbox" id="modeOne" v-model="selectedResponseModes" value="one" />
            <span style="margin-left: 8px;">One Step at a Time</span>
          </div>
          <div style="display: flex; align-items: center; gap: 10px;">
            <button class="primary-button" @click="handleShowVideos" :disabled="selectedAction==='Fix' && !isPremium">
              <span style="font-size: 150%;">🎥</span> video
            </button>
            <button class="primary-button" @click="handleShowPictures" :disabled="selectedAction==='Fix' && !isPremium">
              <span style="font-size: 150%;">📷</span> pictures
            </button>
          </div>
        </div>
        <div v-if="!fullResponse && !oneStepResponse && !videoResponse && !picturesResponse">
          <!-- Updated submit button to open the safety modal -->
          <button class="primary-button" @click="openSafetyModal">Submit</button>
        </div>
        <div v-else>
          <div v-if="selectedResponseModes.includes('all') && fullResponse" class="response-box">
            <p><span class="emoji">{{ responseEmoji }}</span> <span v-html="fullResponse"></span></p>
          </div>
          <div v-if="selectedResponseModes.includes('one') && oneStepResponse" class="response-box">
            <p><span class="emoji">{{ responseEmoji }}</span> <span v-html="oneStepResponse"></span></p>
          </div>
          <!-- Conditionally render dynamic troubleshooting response for premium Fix flow -->
          <div v-if="selectedResponseModes.includes('one')">
            <!-- For Fix with premium, replace yes/no buttons with text input and an added "How Do I Do This?" button -->
            <div v-if="selectedAction === 'Fix' && isPremium" class="diagnostic-response">
              <input type="text" v-model="diagnosticAnswer" placeholder="Type your answer here" />
              <button class="primary-button" @click="submitDiagnosticAnswer">Submit Answer</button>
              <button class="primary-button" @click="sendExplainStep">How Do I Do This?</button>
            </div>
            <div v-else class="step-buttons">
              <button class="primary-button" @click="sendPreviousStep" :disabled="currentStepIndex === 1">Previous Step</button>
              <button class="primary-button" @click="sendExplainStep">How do I do this?</button>
              <button class="primary-button" @click="sendNextStep">Next Step</button>
            </div>
          </div>
          <div v-if="explanationResponse" class="response-box">
            <h3>Explanation:</h3>
            <p v-html="explanationResponse"></p>
          </div>
          <!-- Explanation Feedback Section with Updated Send Method -->
          <div v-if="explanationResponse" class="explanation-feedback">
            <textarea v-model="explanationFeedback" placeholder="Provide additional feedback on this explanation"></textarea>
            <button class="primary-button" @click="sendExplanationFeedback">Send</button>
          </div>
          <div v-if="selectedResponseModes.includes('videos')" class="response-box">
            <h3>Video Links:</h3>
            <p v-html="videoResponse"></p>
          </div>
          <div v-if="selectedResponseModes.includes('pictures')" class="response-box">
            <h3>Relevant Pictures:</h3>
            <p v-html="picturesResponse"></p>
          </div>
        </div>

        <div class="support-buttons" v-if="fullResponse || oneStepResponse || videoResponse || picturesResponse">
          <button class="primary-button" @click="handleCallProfessional">Call a Professional</button>
          <button class="primary-button" @click="toggleMyContractorInput">Call My Contractor</button>
        </div>
        <!-- Added block for premium support UI in Action Details -->
        <div v-if="isPremium && currentSupportType === 'professional'" class="response-box">
          <div class="form-group">
            <label>Enter Zip Code for Professional Search</label>
            <input v-model="callZip" type="text" placeholder="e.g., 12345" />
          </div>
          <!-- Confirm button added below the trade dropdown -->
          <div class="form-group" v-if="showTradeDropdown">
            <label>Select a Trade</label>
            <select v-model="selectedTrade">
              <option disabled value="">Please select a trade</option>
              <option v-for="(option, index) in tradeOptions" :key="index" :value="option.title">
                {{ option.title }}
              </option>
            </select>
            <button class="primary-button" @click="confirmTradeSelection">Confirm Trade</button>
          </div>
          <div class="form-group" v-if="showMyContractorInput">
            <label>Enter Contractor Name</label>
            <input v-model="myContractorNameInput" type="text" placeholder="Enter contractor name" />
            <button class="primary-button" @click="getMyContractorByName">Get My Contractor</button>
          </div>
          <div v-if="myContractor" class="contractor-confirmation">
            <h3>Confirm Contractor Details</h3>
            <p><strong>Name:</strong> {{ myContractor.name }}</p>
            <p><strong>Address:</strong> {{ myContractor.address }}</p>
            <p><strong>Phone:</strong> {{ myContractor.phone }}</p>
            <p><strong>Type:</strong> {{ myContractor.type }}</p>
            <p><strong>Rating:</strong> {{ myContractor.rating }}</p>
            <p><strong>Total Reviews:</strong> {{ myContractor.userRatingsTotal }}</p>
            <a :href="myContractor.googleLink" target="_blank">View on Google</a>
            <button class="primary-button" @click="handleContractorSelection(myContractor)">
              Make My Contractor
            </button>
          </div>
          <div v-if="supportResponse && currentSupportType === 'professional'" class="response-box">
            <h3>Professional Recommendations:</h3>
            <ul>
              <li v-for="(contractor, index) in contractorResults" :key="index">
                <strong>{{ contractor.name }}</strong><br />
                {{ contractor.address }}<br />
                Rating: {{ contractor.rating }} ({{ contractor.userRatingsTotal }} reviews)<br />
                <a :href="contractor.googleLink" target="_blank">View on Google</a><br />
                <button class="primary-button" @click="handleContractorSelection(contractor)">
                  Make My Contractor
                </button>
              </li>
            </ul>
          </div>
        </div>
        <div v-if="errorMessageAction" class="error">{{ errorMessageAction }}</div>
        <div class="reset-button">
          <button class="secondary-button" @click="resetProcess">My Problem is Fixed</button>
        </div>
        <div v-if="showResetOptions" class="reset-options">
          <button class="secondary-button" @click="solveAnotherProblem">Solve Another Problem</button>
          <button class="secondary-button" @click="goToMainScreen">Main Screen</button>
        </div>
      </div>
    </div>

    <!-- Routed Components will render here-->
    <router-view/>



    <!-- Find a New Contractor Search Modal -->
    <div v-if="showContractorForm" class="modal-overlay">
      <div class="modal-content">
        <h3>Find a New Contractor</h3>
        <input v-model="contractorZip" type="text" placeholder="Enter your zip code" />
        <!-- Trade Dropdown -->
        <select v-model="selectedTrade">
          <option disabled value="">Select Trade</option>
         <option v-for="trade in contractorTypes" :key="trade" :value="trade">
           {{ trade }}
         </option>
       </select>
        <button class="primary-button" @click="searchNewContractors">Search</button>
        <button class="secondary-button" @click="showContractorForm = false">Close</button>
      
        <!-- Display Search Results if available -->
        <div v-if="contractorResults && contractorResults.length" style="margin-top: 10px;">
          <h4>Search Results</h4>
          <ul style="list-style-type: disc; padding-left: 20px;">
            <li v-for="contractor in contractorResults" :key="contractor.place_id">
              <strong>{{ contractor.name }}</strong><br>
              {{ contractor.address }}<br>
              Rating: {{ contractor.rating }} ({{ contractor.userRatingsTotal }} reviews)
              <br>
              <a :href="contractor.googleLink" target="_blank">View on Google</a>
              <button class="primary-button" @click="handleContractorSelection(contractor)">
                Make My Contractor
              </button>
            </li>
          </ul>
       </div>
     </div>
   </div>

   <!-- Equipment Modal -->
<div v-if="showEquipmentModal" class="modal-overlay" @click.self="closeEquipmentModal">
  <div class="modal-content">
    <h3>Save Equipment</h3>

    <div class="form-group">
      <label>Enter Equipment Type</label>
      <input
        v-model="equipmentTypeCustom"
        type="text"
        placeholder="e.g., Furnace / Dishwasher / Fan"
      />
    </div>

    <div class="form-group">
      <label>Custom Equipment Name</label>
      <input
        v-model="equipmentName"
        type="text"
        placeholder="e.g., ‘Downstairs Furnace’"
      />
    </div>

    <div class="form-group">
      <label>Equipment Location</label>
      <select v-model="equipmentType">
        <option
          v-for="option in equipmentOptions"
          :key="option"
          :value="option"
        >
          {{ option }}
        </option>
      </select>
    </div>

    <button class="primary-button" @click="saveEquipment">
      Save Equipment
    </button>
    <button class="secondary-button" @click="closeEquipmentModal">
      Cancel
    </button>
  </div>
</div>


   

    <!-- My Contractor Modal -->
  <div v-if="showMyContractorInput" class="modal-overlay">
    <div class="modal-content">
      <h3>Find My Contractor</h3>
      <input v-model="myContractorNameInput" type="text" placeholder="Enter contractor name" />
      <input v-model="callZip" type="text" placeholder="Enter your zip code" />
      <select v-model="selectedTrade">
      <option disabled value="">Select Trade</option>
      <option v-for="trade in contractorTypes" :key="trade" :value="trade">
        {{ trade }}
      </option>
    </select>
      <button class="primary-button" @click="getMyContractorByName">Search</button>
      <button class="secondary-button" @click="showMyContractorInput = false">Cancel</button>
     
      <!-- Display Contractor Search Result if available -->
      <div v-if="myContractor" style="margin-top: 10px;">
        <h4>Contractor Found:</h4>
        <p><strong>{{ myContractor.name }}</strong></p>
        <p>{{ myContractor.address }}</p>
        <p>{{ myContractor.phone }}</p>
        <p>Rating: {{ myContractor.rating }} ({{ myContractor.userRatingsTotal }} reviews)</p>
        <a :href="myContractor.googleLink" target="_blank">View on Google</a>
        <button class="primary-button" @click="handleContractorSelection(myContractor)">
          Make My Contractor
        </button>
      </div>
    </div>
  </div>
    
    

  
    <!-- New Tab Bar Navigation -->
    <nav class="tabbar">
      <template v-if="user || currentScreen === 'freeTrial'">
        <button class="tabbar-item" @click="selectProfile">Profile</button>
        <button class="tabbar-item" @click="selectMyHome">My Home</button>
        <button class="tabbar-item" @click="selectMyBusiness">My Business</button>
        <button class="tabbar-item" @click="selectMyContractors">My Contractor</button>
      </template>
    </nav>

    <!-- Modal Pop-Up for Safety Notice -->
    <div v-if="showSafetyModal" class="modal-overlay">
      <div class="modal-content">
        <p>
          <strong>Important Safety Notice:</strong><br>
          Before beginning any work, please ensure you have all the required protective gear. While Pocket Journeyman offers guidance, it is not infallible. If you have any doubts or concerns, we strongly recommend consulting a qualified professional for assistance.
        </p>
        <button class="primary-button" @click="handleSafetyCallProfessional">Call a Professional</button>
        <button class="primary-button" @click="confirmSafetyContinue">I wish to continue</button>
      </div>
    </div>
  </div>
</template>

<script>
import { loadStripe } from "@stripe/stripe-js";
import axios from "axios";
import Registration from "./components/Registration.vue";
import Questionnaire from "./components/Questionnaire.vue";
import SelectEquipmentModal from '@/components/SelectEquipmentModal.vue';

const API_URL = ''; 

export default {
  components: {
    Registration,
    Questionnaire,
    SelectEquipmentModal
  },
  data() {
    return {
      showRegistrationModal: false, //Controls the visibility of the registration modal
          showPreferencesModal: false, //Controls the visibility of the preferences modal
          preferencesQuestionnaire: {
            accomplish: "",
            otherAccomplish: "",
            toolExperience: "",
            toolsAccess: ""
          },     
      conversationHistory: [], 
     // For storing the logged-in user from DB
      user: null,
      currentScreen: "actionSelection",
      screenHistory: [],
      showMenu: false,
      
      // Profile View form data (for editing existing profile)
      profileViewForm: {
        fullName: "",
        email: "",
        zip: "",
        skillLevel: "",
        occupation: "",
        yearsOfExperience: ""
      },
      // Free trial fields
      trialSkillLevel: "",
      selectedAppliance: "",
      issueDescription: "",
      troubleshootingSteps: "",
      errorMessage: "",
      freeTrialCount: 0,
      freeTrialStepIndex: 1,
      freeTrialNextStepClicks: 0,
      // Login fields
      loginEmail: "",
      loginPassword: "",
      errorMessageLogin: "",
      // Action pathway data
      selectedAction: "",
      actionDescription: "",
      workingOn: "",
      stepsAlreadyTaken: "",
      selectedResponseModes: [],
      fullResponse: "",
      oneStepResponse: "",
      videoResponse: "",
      picturesResponse: "",
      explanationResponse: "",
      errorMessageAction: "",
      currentStepIndex: 1,
      stepHistory: [],
      stepSubmitted: false,
      responseMode: "",
      navigationSelection: {
        previous: false,
        explain: false,
        next: false
      },
      //Equipment Modal
      showSelectEquipmentModal: false,
      // Optional device info fields
      brand: "",
      model: "",
      serial: "",
      // Support response and type
      supportResponse: "",
      currentSupportType: "",
      // Zip code for professional search
      callZip: "",
      errorMessageProfile: "",
      // Dropdown options
      skillLevels: ["Beginner", "Intermediate", "Expert", "Service Technician"],
      appliances: ["Refrigerator", "Freezer", "Dishwasher", "Oven"],
      // Subscription data
      selectedPlan: "Pay as you go",
      promoCode: "",
      subscriptionMessage: "",
      // Credit card fields for billing
      cardName: "",
      billingZip: "",
      // New properties for Stripe integration
      stripe: null,
      cardElement: null,
      stripeInitialized: false,
      paymentMethodId: null,
      // Cancellation fields
      cancellationOptions: [
        "Too expensive",
        "Not satisfied with the features",
        "Found an alternative",
        "Other"
      ],
      selectedCancellationReasons: [],
      cancellationComments: "",
      showCancellationForm: false,
      cancellationMessage: "",
      // Contractor search fields
      showContractorForm: false,
      contractorZip: "",
      contractorResults: [],
      showTradeDropdown: false,
      selectedTrade: "",
      // "My Contractor" functionality
      showMyContractorInput: false,
      myContractorNameInput: "",
      myContractor: null,
      // NEW CONTRACTORS FEATURE:
      savedContractors: [],
      // New data for contractor call-to-action boxes
      contractorTypes: ["HVAC Contractor", "Electrician", "Plumber", "Appliance Repair Technician", "Handyman"],
      selectedContractorType: "",
      showContractorOptions: false,
      // Equipment saving fields
      equipmentName: "",
      equipmentType: "",
      equipmentTypeCustom: "",
      equipmentOptions: ["Home", "Business"],
      // Equipment storage
      savedEquipment: {
        Home: [],
        Business: []
      },
      selectedExistingEquipment: null,
      // Questionnaire for premium users
      questionnaire: {
        accomplish: "",
        toolExperience: "",
        toolsAccess: ""
      },
      // For reset options
      showResetOptions: false,
      // For additional feedback under Explanation
      explanationFeedback: "",
      explanationAdditionalResponse: "",
      // Current equipment location (for equipment view)
      currentEquipmentLocation: "",
      // New data for free trial locked checkboxes
      freeTrialLocked: {
        all: false,
        videos: false,
        pictures: false
      },
      // New property to control the safety notice display
      showSafetyNotice: true,
      // New property to control modal visibility
      showSafetyModal: false,
      showEquipmentModal: false,
      showSelectEquipmentModal: false,
      // New: conversation history for dynamic troubleshooting
      conversationHistory: [],
      // NEW: variable to capture the diagnostic answer for premium Fix troubleshooting
      diagnosticAnswer: "",
      
    };
  },
  computed: {
    passwordValid() {
      const criteria = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;
      return criteria.test(this.profilePassword);
    },
    confirmPasswordValid() {
      return this.profilePassword === this.profileConfirmPassword;
    },
  
    isPremium() {
      const devPremiumEmail = "mjackson201022@gmail.com";
      if (this.user && this.user.subscriptionPlan) {
        return (
          this.user.subscriptionPlan === "Premium/w video" ||
          this.user.email === devPremiumEmail
        );
      }
      return (
        this.selectedPlan === "Premium/w video" ||
        this.profileEmail === devPremiumEmail ||
        this.loginEmail === devPremiumEmail
      );
    },
    tradeOptions() {
      if (
        this.selectedAction === "Fix" ||
        this.selectedAction === "Build" ||
        this.selectedAction === "Install"
      ) {
        return [
          { title: "HVAC Contractor", description: "Handles heating, ventilation, and air conditioning issues." },
          { title: "Electrician", description: "Specializes in electrical repairs and wiring issues." },
          { title: "Plumber", description: "Handles water leaks, clogs, and plumbing repairs." },
          { title: "Appliance Repair Technician", description: "Repairs malfunctioning home appliances." },
          { title: "Handyman", description: "Performs small repairs and maintenance tasks." }
        ];
      } else {
        return [];
      }
    },
    tradeDefinition() {
      const option = this.tradeOptions.find(opt => opt.title === this.selectedTrade);
      return option ? option.description : "";
    },
    combinedEquipment() {
      const all = [];
      if (this.savedEquipment.Home) {
        this.savedEquipment.Home.forEach(eq => {
          all.push({ ...eq, location: "Home" });
        });
      }
      if (this.savedEquipment.Business) {
        this.savedEquipment.Business.forEach(eq => {
          all.push({ ...eq, location: "Business" });
        });
      }
      return all;
    },
    responseEmoji() {
      if (this.selectedAction === "Fix") return "👨🏻‍🔧";
      if (this.selectedAction === "Build") return "👷🏾‍♀️";
      if (this.selectedAction === "Install") return "👷🏿‍♂️";
      return "";
    }
  },
  watch: {
    showRegistrationModal(newVal) {
      console.log("showRegistrationModal changed:", newVal);
    },
    selectedExistingEquipment(newVal) {
      if (!newVal) return;
      this.equipmentName = newVal.name;
      this.equipmentTypeCustom = newVal.type;
      this.brand = newVal.brand || "";
      this.model = newVal.model || "";
      this.serial = newVal.serial || "";
      this.equipmentType = newVal.location;
    },
  },
  methods: {
    async updateProfile() {
      try {
        const payload = {
          fullName:            this.profileViewForm.fullName,
          zipCode:             this.profileViewForm.zip,
          skillLevel:          this.profileViewForm.skillLevel,
          occupation:          this.profileViewForm.occupation,
          yearsOfExperience:   this.profileViewForm.yearsOfExperience
        };
        const resp = await axios.put(
          "/api/user/profile",
          payload,
          { withCredentials: true }
        );
        // update local user and go back to view mode
        this.user = resp.data.user;
        this.navigateTo("profileView");
        alert("Profile updated successfully");
      } catch (err) {
        console.error(err);
        alert(err.response?.data?.error || "Failed to update profile");
      }
    },
    openRegistrationModal() {
      console.log("Create Profile clicked. Opening registration modal.");
      this.showRegistrationModal = true;
    },
    handleRegistered(user) {
      this.user = user;
      this.closeRegistrationModal();
    },
    //Close Registration Modal
    closeRegistrationModal() {
      this.showRegistrationModal = false;
    },
    selectProfile() {
      console.log("selectProfile clicked. Current user:", this.user);
      this.showRegistrationModal = true;
    },
    
    

    logoutUser() {
      //clear token and user data from LocalStorage
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      this.user = null;
      delete axios.defaults.headers.common["Authorization"];
      this.currentScreen = "login";
      alert("You have been logged out.");  
    },
    handlePopState(event) {
      if (event.state && event.state.screen) {
        this.currentScreen = event.state.screen;
      } else {
        this.currentScreen = "actionSelection";
      }
    },
    async initializeStripe() {
      if (this.stripeInitialized) return;
      const cardElement = document.getElementById('card-element');
      if (!cardElement) {
        console.error("Card element not found.");
        return;
      }
    console.log("Checking for #card-element:", document.getElementById('card-element'));
     
    
    this.stripe = await loadStripe('pk_live_51QwcN4JwJ2HmDKTaIf9pPdXkJJ94ZsbTpqI0hssSjL5KnQgm7ti40b2aCrNHTdHyeE7DPpyTXuuuvBqQmP9Dk25Q00ZEcRh9n7');
      console.log("Stripe instance initialized:", this.stripe);
    
    
      const elements = this.stripe.elements();
      this.cardElement = elements.create('card');
      this.cardElement.mount('#card-element');
      this.cardElement.on('change', (event) => {
    
    
        const displayError = document.getElementById('card-errors');
        if (event.error) {
          displayError.textContent = event.error.message;
        } else {
          displayError.textContent = '';
        }
      });
      this.stripeInitialized = true;
    },
    toggleMenu() {
      this.showMenu = !this.showMenu;
    },
    
    handleGlobalClick() {
      if (this.showMenu) {
        this.showMenu = false;
      }
    },
    navigateTo(screen) {
      if(this.showRegistrationModal) {
        console.log("Registration modal open; navigation suppressed.");
        return;
      }
      console.log("Navigating to screen:", screen);
      this.screenHistory.push(this.currentScreen);
      window.history.pushState({ screen: screen }, '', '?screen=' + screen);
      this.currentScreen = screen;
      this.errorMessage = "";
      this.troubleshootingSteps = "";
      this.issueDescription = "";
      this.showMenu = false;
      this.stepSubmitted = false;
      if (!((screen === "actionDetails" || screen === "freeTrial") && this.selectedAction === "Fix")) {
        this.selectedResponseModes = [];
      }
      this.fullResponse = "";
      this.oneStepResponse = "";
      this.videoResponse = "";
      this.picturesResponse = "";
      if (screen === "questionnaire" && this.user && this.user.questionnaire) {
        this.questionnaire = { ...this.user.questionnaire };
      }
    },
    goBack() {
      if (this.screenHistory.length > 0) {
        this.currentScreen = this.screenHistory.pop();
      }
      this.showMenu = false;
    },
    goHome() {
      this.screenHistory = [];
      this.currentScreen = "actionSelection";
      this.showMenu = false;
    },
    selectHome() {
      this.goHome();
    },
    selectBack() {
      this.goBack();
    },
    selectProfile() {
      console.log("selectProfile clicked, user:", this.user);
      if (this.user) {
        console.log("User exists, navigating to profile view");
        this.navigateTo('profile');  
      } else {
        console.log("No user found, opening registration modal");
        this.showRegistrationModal = true;
      }

      if (this.user) {
        this.profileViewForm.fullName = this.user.fullName || "";
        this.profileViewForm.email = this.user.email || "";
        this.profileViewForm.zip = this.user.zipCode || "";
        this.profileViewForm.skillLevel = this.user.skillLevel || "";
        this.profileViewForm.occupation = this.user.occupation || "";
        this.profileViewForm.yearsOfExperience = this.user.yearsOfExperience || "";
        this.navigateTo("profileView");
      } else {
        // If no user is Logged in, open the registration modal
        this.showRegistrationModal = true;
      }
    },
    closeRegistrationModal() {
      this.showRegistrationModal = false;
    },
    openEquipmentModal() {
      this.showEquipmentModal = true;
    },
    closeEquipmentModal() {
      this.showEquipmentModal = false;
    },
    openSelectEquipmentModal() {
      this.showSelectEquipmentModal = true;
    },
    closeSelectEquipmentModal() {
      this.showSelectEquipmentModal = false;
    },
    handleExistingEquipmentSelect(equipment) {
    this.selectedExistingEquipment = equipment;
    this.showSelectEquipmentModal = false;
    },
    selectSubscription() {
      this.navigateTo("subscription");
    },
    selectMyHome() {
      this.navigateTo("myHome");
    },
    selectMyBusiness() {
      this.navigateTo("myBusiness");
    },
    selectMyContractors() {
      this.navigateTo("myContractors");
    },
    async handleContractorSelection(contractor) {
      console.log("Selected contractor:", contractor);
      await this.makeMyContractor(contractor);
    },
    async makeMyContractor(contractor) {
      contractor.type = this.selectedTrade;
      if (!contractor.type || contractor.type.trim() === "") {
        alert("Contractor type is required.");
        return;
      }
      const path = contractor._id || contractor.place_id;
      if (!path) {
        alert("Contractor path (place_id) is required");
        return;
      }
      try {
        const response = await fetch(`/api/user/mycontractor`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include', // ensures session cookie is sent
          body: JSON.stringify({ contractor })
        });
        if (!response.ok) {
          const errorData = await response.json();
          alert(errorData.error || 'Failed to update contractor.');
          return;
        }
        const data = await response.json();
        this.user.myContractor = data.myContractor;
        this.savedContractors = data.myContractor;
        const addedContractor = data.myContractor[data.myContractor.length - 1];
        alert(`Contractor ${addedContractor.name} has been set as your contractor.`);
        this.navigateTo("myContractors");
      } catch (error) {
        alert(`Error updating contractor: ${error.message}`);
      }
    },
    selectMyEquipment() {
      if (this.currentScreen === "myHome") {
        this.currentEquipmentLocation = "Home";
      } else if (this.currentScreen === "myBusiness") {
        this.currentEquipmentLocation = "Business";
      } else {
        this.currentEquipmentLocation = "Home";
      }
      this.navigateTo("equipmentView");
    },
    contractorByType(type) {
      return this.savedContractors.find(contractor => contractor.type === type);
    },
    deleteContractorByType(type) {
      const index = this.savedContractors.findIndex(contractor => contractor.type === type);
      if (index !== -1) {
        this.savedContractors.splice(index, 1);
      }
    },
    openContractorForm(type) {
      console.log("Opening contractor form for type:", type);
      this.selectedContractorType = type;
      this.showContractorOptions = true;
    },
   
      
       async getTroubleshootingSteps() {
        if (this.currentScreen === "freeTrial" && this.freeTrialCount >= 5) {
          this.errorMessage = "Free trial limit reached. Please create a profile and subscribe to continue.";
          return;
        }
      if (!this.issueDescription.trim()) {
        this.errorMessage = "Please describe the issue.";
        return;
      }
      this.errorMessage = "";
      this.troubleshootingSteps = "";
      try {
        const response = await fetch(`/api/troubleshoot`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            skillLevel: this.currentScreen === "freeTrial" ? "Beginner" : (this.trialSkillLevel || this.profileSkillLevel),
            issue: this.issueDescription,
            oneStep: true,
            stepIndex: this.freeTrialStepIndex,
            occupation: this.profileSkillLevel === "Service Technician" ? this.profileOccupation : "",
            yearsOfExperience: this.profileSkillLevel === "Service Technician" ? this.profileYearsOfExperience : "",
            brand: this.brand,
            model: this.model,
            serial: this.serial
          })
        });
        if (!response.ok) throw new Error(`Server error: ${response.statusText}`);
        const data = await response.json();
        this.troubleshootingSteps = data.steps;
        if (this.currentScreen === "freeTrial") {
          this.freeTrialCount++;
        }
      } catch (error) {
        this.errorMessage = `Failed to fetch troubleshooting step: ${error.message}`;
      }
    },
    getNextTrialStep() {
      this.freeTrialStepIndex++;
      this.getTroubleshootingSteps();
    },
    problemSolved() {
      alert("Great! We're glad your problem is solved. Consider signing up for ongoing support.");
      this.navigateTo("home");
    },
    toggleContractorForm() {
      this.showContractorForm = !this.showContractorForm;
    },
    async findContractor() {
      if (!this.contractorZip) {
        alert("Please enter a zip code for contractor search.");
        return;
      }
      try {
        const tradeParam = this.selectedTrade ? `&trade=${encodeURIComponent(this.selectedTrade)}` : "";
        const response = await fetch(
          `/api/contractors?zip=${this.contractorZip}&radius=10&problem=${encodeURIComponent(this.issueDescription)}${tradeParam}`
        );
        if (!response.ok) throw new Error(`Contractor search error: ${response.statusText}`);
        const data = await response.json();
        this.contractorResults = data.contractors || [];
      } catch (error) {
        alert(`Failed to retrieve contractor data: ${error.message}`);
      }
    },
    toggleTradeDropdown() {
      this.showTradeDropdown = !this.showTradeDropdown;
    },
    handleCallProfessional() {
      console.log("handleCallprofessional triggered");
      this.showContractorForm = true;
      this.showContractorOptions = false;
    },
    toggleMyContractorInput() {
      console.log("toggleMyContractorInput triggered");
      this.showMyContractorInput = true;
      this.showContractorOptions = false;
    },
    confirmTradeSelection() {
      if (!this.selectedTrade) {
        alert("Please select a trade.");
        return;
      }
      this.showTradeDropdown = false;
      this.sendProfessionalCall();
    },
    async searchNewContractors() {
      if (!this.callZip) {
        const enteredZip = window.prompt("Please enter your zip code for professional search:");
        if (!enteredZip) {
          alert("A zip code is required for professional search.");
          return;
        }
        this.callZip = enteredZip;
      }
      try {
        const tradeParam = this.selectedTrade ? `&trade=${encodeURIComponent(this.selectedTrade)}` : "";
        const response = await fetch(
          `/api/contractors?zip=${this.callZip}&radius=10&problem=${encodeURIComponent(this.issueDescription)}${tradeParam}`
        );
        if (!response.ok) throw new Error(`Contractor search error: ${response.statusText}`);
        const data = await response.json();
        this.contractorResults = data.contractors || [];
        let formatted = this.contractorResults
          .map(
            contractor =>
              `<li>
                <strong>${contractor.name}</strong> - ${contractor.phone} (Rating: ${contractor.rating} from ${contractor.userRatingsTotal} reviews)
                <br/><a href="${contractor.googleLink}" target="_blank">View on Google</a>
              </li>`
          )
          .join("");
        this.supportResponse = `<ul style="list-style-type:disc; padding-left:20px;">${formatted}</ul>`;
        this.currentSupportType = "professional";
      } catch (error) {
        this.errorMessageAction = `Failed to get professional recommendations: ${error.message}`;
      }
    },
    async getMyContractorByName() {
      if (!this.myContractorNameInput.trim()) {
        alert("Please enter a contractor name.");
        return;
      }
      if (!this.selectedTrade || this.selectedTrade.trim() === "") {
        alert("Please select a contractor type before saving.");
        return;
      }
      try {
        const response = await fetch(
          `/api/contractorByName?name=${encodeURIComponent(this.myContractorNameInput)}&zip=${encodeURIComponent(this.callZip)}`
        );
        if (!response.ok) throw new Error(`Contractor search error: ${response.statusText}`);
        const data = await response.json();
        if (data.contractor) {
          const getValue = (field) =>
            field != null && String(field).trim() !== "" ? field : "Not Available";
          this.myContractor = {
            place_id: data.contractor.place_id,
            name: getValue(data.contractor.name),
            address: getValue(data.contractor.address),
            phone: getValue(data.contractor.phone),
            rating: data.contractor.rating != null ? data.contractor.rating : "Not Available",
            userRatingsTotal:
              data.contractor.userRatingsTotal != null
                ? data.contractor.userRatingsTotal
                : "Not Available",
            googleLink: data.contractor.googleLink || "#"
          };
          this.myContractor.type = this.selectedTrade;
        } else {
          alert("No contractor found with that name.");
        }
      } catch (error) {
        alert(`Failed to retrieve contractor: ${error.message}`);
      }
    },
    async saveContractor(contractor) {
      const response = await fetch(`/api/saveContractor`, {   // ← relative first‑party URL
        method: 'POST',
        mode: 'cors',
        credentials: 'include',                                // ← send session cookie
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contractor })
    });
    const data = await response.json();
    if (!response.ok) {
      alert(data.error || 'Failed to save contractor.');
      return;
    }
    // Update your local state
    this.savedContractors = this.savedContractors || [];
    this.savedContractors.push(data.contractor);
    alert(`Contractor saved: ${data.contractor.name}`);
    },

    async saveEquipment() {
      if (!this.equipmentName.trim() || !this.equipmentType || !this.equipmentTypeCustom.trim()) {
        alert("Please enter a custom equipment name, select an equipment location, and input the equipment type.");
        return;
      }
      const equipmentData = {
        name: this.equipmentName,
        location: this.equipmentType,
        type: this.equipmentTypeCustom,
        brand: this.brand,
        model: this.model,
        serial: this.serial
      };
      try {
        const response = await fetch(`/api/equipment`, {
          method: 'POST',
          mode: 'cors',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(equipmentData)
        });
        const data = await response.json();
        if (!response.ok) {
          alert(data.error || 'Failed to save equipment.');
          return;
        }
        if (!this.savedEquipment[this.equipmentType]) {
          this.savedEquipment[this.equipmentType] = [];
        }
        this.savedEquipment[this.equipmentType].push(data.equipment);
        alert(`Equipment saved: ${this.equipmentName} - Location: ${this.equipmentType}`);
        this.closeEquipmentModal();
      } catch (error) {
        alert('Error saving equipment: ' + error.message);
      }
      this.equipmentName = "";
      this.equipmentType = "";
      this.equipmentTypeCustom = "";
      this.brand = "";
      this.model = "";
      this.serial = "";
    },
    async fetchUserEquipment() {
      if (!this.user || !this.user.id) return;
      try {
        const response = await fetch(
          `/api/equipment/${this.user.id}`,
          {
            method: 'GET',
            mode: 'cors',
            credentials: 'include'
          }
        );
        const data = await response.json();
        if (response.ok) {
          let equipmentByLocation = { Home: [], Business: [] };
          data.equipment.forEach(eq => {
            if (eq.location === "Home" || eq.location === "Business") {
              equipmentByLocation[eq.location].push(eq);
            } else {
              equipmentByLocation["Home"].push(eq);
            }
          });
          this.savedEquipment = equipmentByLocation;
        } else {
          console.error(data.error);
        }
      } catch (error) {
        console.error('Error fetching equipment: ' + error.message);
      }
    },
    async sendVideoRequest() {
      // 1. Grab the ChatGPT instruction—nothing else.
      const query = this.explanationResponse?.trim();

      // 2. If it’s not there yet, ask the user to wait.
      if (!query) {
        alert("Please wait for the Pocket Journeyman instruction before searching for videos.");
        return;
      }

      console.log("Using ChatGPT instruction for video query:", query);

      try {
        // 3. Hit your backend with stepInstruction=query, plus a cache buster.
        const response = await fetch(
          `/api/videos?stepInstruction=${encodeURIComponent(query)}&t=${Date.now()}`
        );
        if (!response.ok) 
          throw new Error(`YouTube API error: ${response.statusText}`);

        const data = await response.json();

        // 4. Render the returned videos (or a “not found” message)
        if (data.videos?.length) {
          const formatted = data.videos
            .map(item => `<li><a href="${item.url}" target="_blank">${item.title}</a></li>`)
            .join("");
          this.videoResponse = `<ul style="list-style-type:disc; padding-left:20px;">${formatted}</ul>`;
        } else {
          this.videoResponse = `<p>No videos found for "${query}".</p>`;
        }
      } catch (error) {
        this.errorMessageAction = `Error fetching videos: ${error.message}`;
      }
    },

    async sendPicturesRequest() {
      const query =
        this.selectedExistingEquipment
          ? `${this.selectedExistingEquipment.name} ${this.selectedExistingEquipment.type} ${this.issueDescription}`
          : this.issueDescription.trim() || this.actionDescription.trim();
      if (!query) {
        alert("Please describe the issue or select your equipment to search for images.");
        return;
      }
      try {
        const response = await fetch(
           `/api/images?query=${encodeURIComponent(query)}`,
          {
            mode: 'cors',
            credentials: 'include',
          }
        );
        if (!response.ok) throw new Error(`Image API error: ${response.statusText}`);
        const data = await response.json();
        if (data.images && data.images.length > 0) {
          let formatted = data.images
            .map((img, index) =>
              `<div class="image-container" key="${index}">
                <a href="${img.link}" target="_blank">
                  <img src="${img.link}" alt="${img.title}" style="max-width:100%;"/>
                </a>
                <p>${img.title}</p>
              </div>`
            )
            .join("");
          this.picturesResponse = `<div class="image-results">${formatted}</div>`;
        } else {
          this.picturesResponse = "<p>No images found.</p>";
        }
      } catch (error) {
        console.error("Error fetching images:", error);
        this.picturesResponse = `<p>Error fetching images: ${error.message}</p>`;
      }
    },
    async sendActionRequestFull() {
      try {
        const response = await fetch(`/api/action`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: this.selectedAction || "Build",
            description: this.issueDescription || this.actionDescription,
            skillLevel: this.currentScreen === "freeTrial" ? "Beginner" : this.profileSkillLevel,
            occupation: this.profileSkillLevel === "Service Technician" ? this.profileOccupation : "",
            yearsOfExperience: this.profileSkillLevel === "Service Technician" ? this.profileYearsOfExperience : "",
            brand: this.brand,
            model: this.model,
            serial: this.serial,
            oneStep: false,
            stepIndex: 0,
            previousSteps: this.stepsAlreadyTaken,
            history: this.stepHistory
          })
        });
        if (!response.ok) throw new Error(`Server error: ${response.statusText}`);
        this.fullResponse = "";
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          const token = decoder.decode(value, { stream: true });
          this.fullResponse += token;
        }

        this.explanationResponse = this.fullResponse;
        console.log("this.explanationResponse ready:", this.explanationResponse);

       
      
      
      } catch (error) {
        this.errorMessageAction = `Failed to get full response: ${error.message}`;
      }
    },
    async sendActionRequestOne() {
      try {
        const response = await fetch(`/api/action`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: this.selectedAction || "Build",
            description: this.issueDescription || this.actionDescription,
            skillLevel: this.currentScreen === "freeTrial" ? "Beginner" : this.profileSkillLevel,
            occupation: this.profileSkillLevel === "Service Technician" ? this.profileOccupation : "",
            yearsOfExperience: this.profileSkillLevel === "Service Technician" ? this.profileYearsOfExperience : "",
            brand: this.brand,
            model: this.model,
            serial: this.serial,
            oneStep: true,
            stepIndex: this.currentStepIndex,
            previousSteps: this.stepsAlreadyTaken
          })
        });
        if (!response.ok) throw new Error(`Server error: ${response.statusText}`);
        this.oneStepResponse = "";
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          const token = decoder.decode(value, { stream: true });
          this.oneStepResponse += token;
        }
        this.stepHistory[this.currentStepIndex - 1] = this.oneStepResponse;
        await this.fetchImagesBasedOnResponse(this.oneStepResponse);
      } catch (error) {
        this.errorMessageAction = `Failed to get one-step response: ${error.message}`;
      }
    },
    async sendActionForSelectedModes() {
      this.fullResponse = "";
      this.oneStepResponse = "";
      this.videoResponse = "";
      this.picturesResponse = "";
      if (this.selectedResponseModes.includes("all")) {
        await this.sendActionRequestFull();
      }
      if (this.selectedResponseModes.includes("one")) {
        await this.sendActionRequestOne();
        await this.fetchImagesBasedOnResponse(this.oneStepResponse);
    
      }
      if (this.selectedResponseModes.includes("pictures")) {
        await this.sendPicturesRequest();
      }
    },
    async sendNextStep() {
      if (this.showSafetyNotice) {
        this.showSafetyNotice = false;
        this.oneStepResponse = "";
      }
      this.conversationHistory.push({
        role: "user",
        content: this.issueDescription || this.actionDescription
      });
      this.explanationResponse = "";
      this.currentStepIndex++;
      this.errorMessageAction = "";
      try {
        const response = await fetch(`/api/action`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: this.selectedAction || "Build",
            description: this.issueDescription || this.actionDescription,
            skillLevel: this.currentScreen === "freeTrial" ? "Beginner" : this.profileSkillLevel,
            occupation: this.profileSkillLevel === "Service Technician" ? this.profileOccupation : "",
            yearsOfExperience: this.profileSkillLevel === "Service Technician" ? this.profileYearsOfExperience : "",
            brand: this.brand,
            model: this.model,
            serial: this.serial,
            oneStep: true,
            stepIndex: this.currentStepIndex,
            previousSteps: this.stepsAlreadyTaken,
            history: this.conversationHistory
          })
        });
        if (!response.ok) throw new Error(`Server error: ${response.statusText}`);
        this.oneStepResponse = "";
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          const token = decoder.decode(value, { stream: true });
          this.oneStepResponse += token;
        }
        this.conversationHistory.push({
          role: "assistant",
          content: this.oneStepResponse
        });
        this.stepHistory[this.currentStepIndex - 1] = this.oneStepResponse;
        await this.fetchImagesBasedOnResponse(this.oneStepResponse);
      } catch (error) {
        this.errorMessageAction = `Failed to get next step: ${error.message}`;
      }
    },
    async sendExplainStep() {
      console.log("sendExplainStep triggered");
      this.errorMessageAction = "";
      const diagnosticQuestion = this.oneStepResponse;
      if (!diagnosticQuestion) {
        this.errorMessageAction = "No diagnostic question available to explain.";
        return;
      }
      const explanationPrompt = `The diagnostic question is: "${diagnosticQuestion}". Provide a detailed, step-by-step guide (formatted as a numbered list) on how to perform the action described in the diagnostic question. Do not include any extra commentary.`;
      try {
        const response = await fetch(`/api/action`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "Explain",
            description: explanationPrompt,
            skillLevel: this.currentScreen === "freeTrial" ? "Beginner" : this.profileSkillLevel,
            occupation: this.profileSkillLevel === "Service Technician" ? this.profileOccupation : "",
            yearsOfExperience: this.profileSkillLevel === "Service Technician" ? this.profileYearsOfExperience : "",
            brand: this.brand,
            model: this.model,
            serial: this.serial,
            oneStep: true,
            stepIndex: this.currentStepIndex,
            previousSteps: this.stepsAlreadyTaken,
            supportType: "explain"
          })
        });
        if (!response.ok) throw new Error(`Server error: ${response.statusText}`);
        this.explanationResponse = "";
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          const token = decoder.decode(value, { stream: true });
          this.explanationResponse += token;
        }
        console.log("Received detailed explanation:", this.explanationResponse);
      } catch (error) {
        this.errorMessageAction = `Failed to get detailed explanation: ${error.message}`;
        console.error("sendExplainStep error:", error);
      }
    },
    async sendExplanationFeedback() {
      try {
        const response = await fetch(`/api/explanationFeedback`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            explanationFeedback: this.explanationFeedback,
            explanationResponse: this.explanationResponse,
            equipmentName: this.equipmentName,
            equipmentType: this.equipmentType,
            equipmentTypeCustom: this.equipmentTypeCustom,
            brand: this.brand,
            model: this.model,
            serial: this.serial,
            stepsAlreadyTaken: this.stepsAlreadyTaken,
            actionDescription: this.actionDescription
          })
        });
        if (!response.ok) throw new Error("Error sending explanation feedback");
        this.explanationResponse = "";
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          const token = decoder.decode(value, { stream: true });
          this.explanationResponse += token;
        }
        this.explanationFeedback = "";
      } catch (error) {
        console.error("Error sending explanation feedback:", error);
      }
    },

    async fetchVideosBasedOnResponse() {
      // 1️⃣ Use the one‑step response if available; otherwise fall back to the full explanation.
      const query = this.oneStepResponse?.trim() || this.explanationResponse?.trim();

      // 2️⃣ Guard: stop if there’s no instruction yet.
      if (!query) {
        this.videoResponse = "<p>No step text provided for video search.</p>";
        return;
      }

      try {
        // 3️⃣ Fetch videos based solely on the current step instruction.
        const res = await fetch(
        `/api/videos?stepInstruction=${encodeURIComponent(query)}`,//
          {
            mode: 'cors',
            credentials: 'include',
          }
        );
        if (!res.ok) throw new Error(`YouTube API error: ${res.statusText}`);
        const { videos } = await res.json();

        // 4️⃣ Render results (or “not found” message).
        if (videos?.length) {
          const formatted = videos
            .map(item => `<li><a href="${item.url}" target="_blank">${item.title}</a></li>`)
            .join("");
          this.videoResponse = `<ul style="list-style-type:disc; padding-left:20px;">${formatted}</ul>`;
        } else {
          this.videoResponse = `<p>No videos found for "${query}".</p>`;
        }
      } catch (error) {
        console.error("Error fetching videos:", error);
        this.videoResponse = `<p>Error fetching videos: ${error.message}</p>`;
      } 
    },



    async fetchImagesBasedOnResponse(responseText) {
      try {
        const res = await fetch(
          `/api/images?responseText=${encodeURIComponent(responseText)}`, //
          {
            mode: 'cors',
            credentials: 'include',
          }
        );
        if (!res.ok) {
          throw new Error(`Image API error: ${res.statusText}`);
        }
        const data = await res.json();
        if (data.images && data.images.length > 0) {
          let formatted = data.images
            .map((img, index) =>
              `<div class="image-container" key="${index}">
                <a href="${img.link}" target="_blank">
                  <img src="${img.link}" alt="${img.title}" style="max-width:100%;"/>
                </a>
                <p>${img.title}</p>
              </div>`
            )
            .join("");
          this.picturesResponse = `<div class="image-results">${formatted}</div>`;
        } else {
          this.picturesResponse = "<p>No images found.</p>";
        }
      } catch (error) {
        console.error("Error updating images:", error);
        this.picturesResponse = `<p>Error updating images: ${error.message}</p>`;
      }
    },
    openSafetyModal() {
      this.showSafetyModal = true;
    },
    confirmSafetyContinue() {
      this.showSafetyModal = false;
      this.sendActionForSelectedModes();
    },
    handleSafetyCallProfessional() {
      this.showSafetyModal = false;
      this.handleCallProfessional();
    },
    handleShowVideos() {
      console.log("handleShowVideos ➔ explanationResponse:", this.explanationResponse);

      if (!this.selectedResponseModes.includes('videos')) {
        this.selectedResponseModes.push('videos');
      }
      this.fetchVideosBasedOnResponse();
    },
    handleShowPictures() {
      if (!this.selectedResponseModes.includes('pictures')) {
        this.selectedResponseModes.push('pictures');
      }
      this.sendPicturesRequest();
    },
    async submitQuestionnaire(questionnaireData) {
      try {
        // 1️⃣ Persist updated questionnaire to backend
        const res = await fetch('/api/user/questionnaire', {
          method: 'PUT',
          mode: 'cors',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ questionnaire: questionnaireData })
        });
        if (!res.ok) throw new Error(`Save failed: ${res.statusText}`);

        // 2️⃣ Read back any success message
        const data = await res.json();

        // 3️⃣ Update local state
        this.user.questionnaire = { ...questionnaireData };

        // 4️⃣ Close the preferences modal
        this.showPreferencesModal = false;

        // 5️⃣ Give feedback
        alert(data.message || 'Preferences saved successfully.');
      } catch (error) {
        console.error('Error saving questionnaire:', error);
        alert('Error saving preferences: ' + error.message);
      }
    },

selectPreferences() {
    console.log("navigating to preferences");

    //Load the saved questionnaire into the modal
    this.preferencesQuestionnaire = { ...this.user.questionnaire };

    //Show the preferences modal
    this.showPreferencesModal = true;
  },


    confirmCancel() {
      this.showCancellationForm = true;
    },
    async submitCancellation() {
    try {
      // 6.2a Call your cancel‐subscription endpoint
      const res = await fetch('/api/subscription/cancel', {
        method: 'POST',
        credentials: 'include'
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error);

      // 6.2b Show Stripe’s confirmation message
      this.cancellationMessage = json.message;

      // 6.2c Update your in-component user state
      this.user.subscriptionPlan       = 'Free';
      this.user.subscriptionStatus     = json.status;
      this.user.stripeSubscriptionId   = null;

      // 6.2d Reset the form
      this.selectedCancellationReasons = [];
      this.cancellationComments        = "";
      this.showCancellationForm        = false;
      this.navigateTo('subscription');
    } catch (err) {
      console.error('Cancel failed:', err);
      this.cancellationMessage = 'Cancellation failed: ' + err.message;
    }
  },

    async loginUser() {
      if (!this.loginEmail.trim() || !this.loginPassword.trim()) {
        this.errorMessageLogin = "Please enter both email and password.";
        return;
      }
      console.log("Attempting login with:", this.loginEmail, this.loginPassword);  
      try {
        // Dispatch the Vuex login action instead of calling fetch directly
        const result = await this.$store.dispatch("login", { 
          email: this.loginEmail, 
          password: this.loginPassword 
        });
        

        alert(result.message || "Login successful!");
        this.user = result.user;

        // store token and user data in local storage
        if (result.token) {
          localStorage.setItem("token", result.token);
          localStorage.setItem("user", JSON.stringify(result.user));
        }
        

        if (this.user.zipCode) {
          this.callZip = this.user.zipCode;
        }
        this.profileEmail = this.user.email;
        this.profileSkillLevel = this.user.skillLevel;
        this.profileOccupation = this.user.occupation;
        this.profileYearsOfExperience = this.user.yearsOfExperience;
        this.selectedPlan = this.user.subscriptionPlan;
        if (this.user.myContractor) {
          this.savedContractors = this.user.myContractor;
        }
        await this.fetchUserEquipment();
        this.goHome();
        this.navigateTo("actionSelection");
      } catch (error) {
        this.errorMessageLogin = error.message;
      }
    },
    chooseAction(action) {
      if (!this.user) {
        this.navigateTo("home");
        return;
      }
      this.selectedAction = action;
      if (action === "Fix") {
        this.selectedResponseModes = ["one"];
        this.conversationHistory = [];
        this.stepsAlreadyTaken = "";
      } else {
        this.selectedResponseModes = [];
      }
      this.actionDescription = "";
      this.fullResponse = "";
      this.oneStepResponse = "";
      this.videoResponse = "";
      this.picturesResponse = "";
      this.explanationResponse = "";
      this.supportResponse = "";
      this.errorMessageAction = "";
      this.currentStepIndex = 1;
      this.stepHistory = [];
      this.stepSubmitted = false;
      this.navigationSelection.previous = false;
      this.navigationSelection.explain = false;
      this.navigationSelection.next = false;
      this.brand = "";
      this.model = "";
      this.serial = "";
      this.callZip = "";
      this.currentSupportType = "";
      this.selectedTrade = "";
      this.showTradeDropdown = false;
      this.showMyContractorInput = false;
      this.myContractorNameInput = "";
      this.myContractor = null;
      this.equipmentName = "";
      this.equipmentType = "";
      this.equipmentTypeCustom = "";
      this.selectedExistingEquipment = null;
      this.showSafetyNotice = true;
      if (this.selectedResponseModes.includes("one")) {
        this.oneStepResponse = `<strong>Important Safety Notice:</strong><br>Before beginning any work, please ensure you have all the required protective gear. While Pocket Journeyman offers guidance, it is not infallible. If you have any doubts or concerns, we strongly recommend consulting a qualified professional for assistance.`;
      }
      this.navigateTo("actionDetails");
    },
    tryForFree() {
      this.selectedAction = "Fix";
      this.selectedResponseModes = ["one"];
      this.conversationHistory = [];
      this.stepsAlreadyTaken = "";
      this.navigateTo("freeTrial");
    },
    lockCheckbox(mode, event) {
      if (this.currentScreen === 'freeTrial') {
        event.preventDefault();
        this.freeTrialLocked[mode] = true;
      }
    },
    handleFreeTrialNextStep() {
      if (this.freeTrialNextStepClicks < 2) {
        this.freeTrialNextStepClicks++;
        this.sendNextStep();
      } else if (this.freeTrialNextStepClicks === 2) {
        this.freeTrialNextStepClicks++;
        this.showRegistrationModal = true;
      }
    },
    freeTrialNavigateToProfile() {
      this.showRegistrationModal = true;
    },
    resetProcess() {
      this.actionDescription = "";
      this.fullResponse = "";
      this.oneStepResponse = "";
      this.videoResponse = "";
      this.picturesResponse = "";
      this.explanationResponse = "";
      this.supportResponse = "";
      this.errorMessageAction = "";
      this.currentStepIndex = 1;
      this.stepHistory = [];
      this.stepSubmitted = false;
      this.navigationSelection.previous = false;
      this.navigationSelection.explain = false;
      this.navigationSelection.next = false;
      this.brand = "";
      this.model = "";
      this.serial = "";
      this.callZip = "";
      this.currentSupportType = "";
      this.selectedResponseModes = [];
      this.selectedTrade = "";
      this.showTradeDropdown = false;
      this.showMyContractorInput = false;
      this.myContractorNameInput = "";
      this.myContractor = null;
      this.showResetOptions = true;
    },
    solveAnotherProblem() {
      this.workingOn = "";
      this.showResetOptions = false;
    },
    goToMainScreen() {
      this.showResetOptions = false;
      this.navigateTo("actionSelection");
    },
    submitDiagnosticAnswer() {
      if (this.diagnosticAnswer.trim() !== "") {
        this.appendConversation(this.diagnosticAnswer);
        this.diagnosticAnswer = "";
      }
    },
    appendConversation(answer) {
      this.conversationHistory.push(answer);
      this.stepsAlreadyTaken = this.conversationHistory.join(" | ");
      this.sendNextStep();
    },
    handleYesResponse() {
      this.appendConversation("Yes");
    },
    handleNoResponse() {
      this.appendConversation("No");
    },
    handleDontKnowResponse() {
      this.appendConversation("I don't know");
    },

    async fetchUser() {
      try {
        const response = await fetch(`/api/user`, {
          method: "GET",
          credentials: "include",
          headers: { "Content-Type": "application/json" }
        });
        if(!response.ok) {
          throw new Error("Failed to fetch user data");
        }
        const userData = await response.json();
        this.user = userData;
        console.log("Fetched user:", this.user);
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    }
  },
  created() {
    axios.defaults.withCredentials = true;     // ← send cookies on all Axios calls

    const token = localStorage.getItem("token");
    if (token) {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        this.user = JSON.parse(storedUser);
      }

      this.fetchUser();

      // Set the token as the default Authorization header for all requests
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      this.currentScreen = "actionSelection";
      this.navigateTo("actionSelection");
    }
  },
  mounted() {
    console.log("Registration component mounted");
    if(this.registrationStep === 3) {
      this.initializeStripe();
    }
    document.addEventListener("click", this.handleGlobalClick);
    window.addEventListener('popstate', this.handlePopState);
  },
  
  beforeUnmount() {
    document.removeEventListener("click", this.handleGlobalClick);
    window.removeEventListener('popstate', this.handlePopState);
  }
};
</script>

<style>
/* Modern Look – Minimalist, Clean, and Flat with Subdued Greens and Oranges using Helvetica */
body {
  background: linear-gradient(135deg, #e0e0e0, #f7f7f7);
  font-family: Helvetica, sans-serif;
  color: #333;
  margin: 0;
  padding: 0;
}
#app {
  background: transparent;
}
.centered-card {
  max-width: 500px;
  margin: 40px auto;
  padding: 30px 40px;
  background: #f2f2f2;
  border: 1px solid #d0d0d0;
  border-radius: 12px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  transition: transform 0.3s ease;
}
.centered-card:hover {
  transform: translateY(-5px);
}
header {
  text-align: center;
  padding: 20px 0;
  background: #8fae76;
  border-bottom: 1px solid #7a9c64;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}
header h1 {
  margin: 0;
  font-size: 32px;
  font-weight: 400;
  color: #000;
  font-family: Helvetica, sans-serif;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
}
header p {
  margin: 8px 0 0;
  font-size: 16px;
  color: #000;
}
.form-group {
  margin-bottom: 15px;
}
.form-group label {
  display: block;
  font-size: 14px;
  margin-bottom: 5px;
  color: #555;
}
input[type="text"],
input[type="email"],
input[type="password"],
select,
textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 14px;
  color: #333;
  background: #fff;
}
.primary-button {
  background: #f4a261;
  color: #000;
  border: none;
  padding: 10px 20px;
  font-size: 14px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.3s ease, color 0.3s ease, transform 0.3s ease;
  margin: 5px;
}
.primary-button:hover {
  background: #e0884d;
  color: #000;
  transform: translateY(-2px);
}
.secondary-button {
  background: #fff;
  color: #f4a261;
  border: 1px solid #f4a261;
  padding: 10px 20px;
  font-size: 14px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.3s ease, color 0.3s ease, transform 0.3s ease;
  margin: 5px;
}
.secondary-button:hover {
  background: #f4a261;
  color: #000;
  transform: translateY(-2px);
}
.menu-container {
  position: relative;
  padding: 10px;
}
.menu-button {
  background: #f4a261;
  color: #000;
  border: none;
  font-size: 20px;
  padding: 10px;
  border-radius: 8px;
  cursor: pointer;
}
.dropdown {
  position: absolute;
  top: 50px;
  left: 10px;
  background: #ffffff;
  border: 1px solid #ccc;
  border-radius: 8px;
  padding: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}
.dropdown-item {
  display: block;
  width: 100%;
  background: none;
  border: none;
  text-align: left;
  padding: 8px 0;
  font-family: Helvetica, sans-serif;
  font-size: 16px;
  color: #000;
  cursor: pointer;
}
.response-box {
  min-height: 100px;
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #ccc;
  padding: 12px;
  margin-top: 15px;
  background: #fafafa;
  border-radius: 8px;
  font-size: 16px;
  line-height: 1.6;
  font-family: Helvetica, sans-serif;
}
.response-box ul {
  list-style-type: disc;
  margin: 0;
  padding-left: 20px;
}
.response-box li {
  margin-bottom: 8px;
}
.step-buttons,
.support-buttons {
  display: flex;
  justify-content: space-around;
  margin-top: 15px;
}
.reset-button {
  text-align: center;
  margin-top: 15px;
}
.reset-options {
  text-align: center;
  margin-top: 10px;
}
.explanation-feedback textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 14px;
  color: #333;
  background: #fff;
  margin-top: 5px;
}
.error {
  color: #d32f2f;
  margin-top: 10px;
  font-size: 14px;
}
.action-options {
  display: flex;
  flex-direction: row;
  gap: 20px;
  margin-top: 20px;
  justify-content: center;
}
.action-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
}
.action-option p {
  text-align: center;
  font-size: 16px;
  margin: 0;
}
.premium-info {
  background: #fff7e6;
  padding: 10px;
  border: 1px solid #f4a261;
  border-radius: 6px;
  margin-bottom: 15px;
  font-style: italic;
  color: #8a5d2c;
}
.locked {
  opacity: 0.6;
  pointer-events: none;
  position: relative;
}
.locked::after {
  content: "Premium Only";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(255, 255, 255, 0.8);
  padding: 5px 10px;
  border: 1px solid #f4a261;
  border-radius: 4px;
  font-size: 14px;
  color: #8a5d2c;
}
body,
input,
textarea,
select,
button {
  transition: all 0.3s ease;
}
.explanation-box {
  margin-top: 15px;
  border: 1px solid #ccc;
  background: #eef;
  padding: 12px;
  border-radius: 8px;
  font-size: 16px;
  line-height: 1.6;
  font-family: Helvetica, sans-serif;
}
input[type="checkbox"][id^="mode"] {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  width: 25px;
  height: 25px;
  border: 3px solid white;
  border-radius: 8px;
  background: white;
  position: relative;
  cursor: pointer;
}
input[type="checkbox"][id^="mode"]:checked {
  background: #4CAF50;
}
input[type="checkbox"][id^="mode"]:checked:after {
  content: "✓";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 28px;
  color: white;
}
.free-trial-locked-container {
  position: relative;
  display: inline-block;
}
.free-trial-locked-container .red-x {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 28px;
  color: red;
  pointer-events: none;
}
.image-results {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
  margin-top: 10px;
}
.image-container {
  max-width: 150px;
  text-align: center;
}
.callout-box {
  flex: 1 1 200px;
  max-width: 220px;
  text-align: center;
  background-color: white;
  padding: 15px;
  margin: 5px 0;
  border-radius: 8px;
  border: none;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}
.callout-box .primary-button {
  font-weight: bold;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}
.profile-callouts {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 20px;
}
.tabbar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #8fae76;
  display: flex;
  justify-content: space-around;
  padding: 10px 0;
  border-top: 1px solid #7a9c64;
  box-shadow: 0 -4px 8px rgba(0, 0, 0, 0.1);
  z-index: 1000;
}
.tabbar-item {
  background: none;
  border: none;
  color: #fff;
  font-size: 16px;
  cursor: pointer;
}
.tabbar-item:focus {
  outline: none;
}
.transparent-callout {
  background: #a0c08c;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  display: inline-block;
}
.transparent-callout h1 {
  font-weight: bold;
}
.emoji {
  font-size: 72px;
  text-align: center;
  margin-bottom: 10px;
}
.response-box .emoji {
  font-size: 36px;
}
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}
.modal-content {
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  max-width: 400px;
  width: 90%;
  text-align: center;
}
.modal-content p {
  margin-bottom: 20px;
  font-size: 16px;
}
.welcome-text {
  text-align: center;
  margin-bottom: 1rem;
}
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  padding: 24px;
  border-radius: 8px;
  max-width: 600px;
  width: 90%;
  box-shadow: 0 2px 10px rgba(0,0,0,0.2);
  max-height: 80vh;
  overflow-y: auto;
  font-weight: bold;
}
.modal-content label {
  font-size: 1.1rem;
}
.modal-content input,
.modal-content select,
.modal-content textarea {
  font-weight: normal;
  font-size: 1rem;
}
</style>